/**
 * The pure half of `MultiSelect`, split out so it can be tested without a DOM —
 * the same split `combobox.logic.ts` makes for `Combobox`.
 */

export interface MultiSelectOption {
  id: string;
  label: string;
}

/**
 * The selection with `id` ticked or unticked, IN THE OPTIONS' ORDER.
 *
 * The order is the list's, not the order of the clicks, so ticking Bogor then
 * Jakarta Barat and ticking them the other way round are the same selection —
 * the same query string and the same react-query cache key, rather than two
 * entries holding one answer.
 *
 * AN ID THE LIST DOES NOT CONTAIN IS KEPT, at the end. The list may still be
 * loading, or the region may have been deactivated since it was picked; either
 * way it is narrowing the report, and toggling a different box must not quietly
 * widen the report by dropping a filter the reader cannot see.
 */
export function toggleSelection(
  options: readonly MultiSelectOption[],
  selected: readonly string[],
  id: string,
): string[] {
  const chosen = new Set(selected);
  if (chosen.has(id)) chosen.delete(id);
  else chosen.add(id);

  const listed = new Set(options.map((option) => option.id));
  return [
    ...options.filter((option) => chosen.has(option.id)).map((option) => option.id),
    ...[...chosen].filter((value) => !listed.has(value)),
  ];
}

/**
 * The searchable picker's selection with `option` ticked or unticked.
 *
 * OPTIONS, NOT IDS, for the reason `Combobox` holds one: the rows arrive a page
 * of search results at a time, so the list that would name a chosen id is gone
 * the moment the search text changes. The selection has to carry its own labels.
 *
 * Ticking appends, so the order is the order they were picked in — there is no
 * whole list here to keep them in the order of. The wire and the cache key sort
 * nothing either; a reader who picks the same two in the other order simply
 * issues one extra request.
 */
export function toggleOption<T extends MultiSelectOption>(selected: readonly T[], option: T): T[] {
  return selected.some((chosen) => chosen.id === option.id)
    ? selected.filter((chosen) => chosen.id !== option.id)
    : [...selected, option];
}

/**
 * The rows the searchable picker lists: EVERY chosen option first, then the
 * search results that are not already among them.
 *
 * PINNED because unticking has to be possible without finding the row again. A
 * chosen outlet is rarely in the twenty results of whatever was typed last, and a
 * picker whose selection can only be undone by re-searching for each name is one
 * that gets cleared wholesale instead.
 */
export function pinnedRows<T extends MultiSelectOption>(
  results: readonly T[],
  selected: readonly T[],
): T[] {
  const chosen = new Set(selected.map((option) => option.id));
  return [...selected, ...results.filter((option) => !chosen.has(option.id))];
}

/**
 * What the closed control says: the "all" wording for no selection, the
 * option's own label for one, a count for more.
 *
 * A COUNT RATHER THAN THE JOINED NAMES. Two `900005 - JAKARTA BARAT`-length
 * labels already overflow the control on a phone, and a truncated list reads as
 * the whole of it — "JAKARTA BARAT, BOG…" hides whether a third was chosen. The
 * names are one tap away, in the open list, with their boxes ticked.
 */
export function selectionSummary(
  options: readonly MultiSelectOption[],
  selected: readonly string[],
  allLabel: string,
  noun: string,
): string {
  if (selected.length === 0) return allLabel;
  if (selected.length === 1) {
    return options.find((option) => option.id === selected[0])?.label ?? `1 ${noun} dipilih`;
  }
  return `${selected.length} ${noun} dipilih`;
}
